import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { useAdminStore, useFeature } from './adminStore';
import { ROUTE_FEATURE_MAP, homeFor } from '@/lib/nav.config';
import { FEATURE_MAP } from './featureRegistry';
import { useToast } from '@/components/ui/Toast';

/**
 * RouteGuard ensures a route is accessible based on feature enablement and hidden menu flags.
 * If disabled or hidden, redirects gracefully to role home and displays a Toast.
 * If in demo mode, injects a subtle demo mode indicator ribbon.
 */
export function RouteGuard({ children }) {
  const location = useLocation();
  const { role } = useSession();
  const store = useAdminStore();
  const toast = useToast();

  const featureId = ROUTE_FEATURE_MAP[location.pathname];
  const { enabled, mode } = useFeature(featureId || 'dashboards');
  const isHidden = role && store.hiddenMenus?.[role]?.includes(location.pathname);

  const isBlocked = enabled === false || isHidden;

  useEffect(() => {
    if (isBlocked) {
      const featMeta = featureId ? FEATURE_MAP.get(featureId) : null;
      const label = featMeta?.label || 'This section';
      toast.show(`${label} is currently disabled in the Admin Console`, { variant: 'error' });
    }
  }, [isBlocked, featureId, toast]);

  if (isBlocked) {
    const fallbackPath = homeFor(role);
    if (fallbackPath !== location.pathname) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  const showDemoRibbon = store.demoRibbon !== false && mode === 'demo';

  return (
    <>
      {showDemoRibbon && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--butter)',
            color: 'var(--butter-ink)',
            fontSize: 'var(--text-xs)',
            fontWeight: 800,
            padding: '2px 10px',
            borderRadius: 'var(--radius-pill)',
            marginBottom: 'var(--space-2)',
            alignSelf: 'flex-start',
            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase'
          }}
          title="Data source is set to Demo in the Admin Console"
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />
          Demo Mode
        </div>
      )}
      {children}
    </>
  );
}
